import { Fragment, useEffect, useState } from "react";
import { getAdminHeaders } from "./adminApi";

const STATUS_OPTIONS = [
  { value: "", label: "全部状态" },
  { value: "pending", label: "待评估" },
  { value: "contacting", label: "沟通中" },
  { value: "approved", label: "已通过" },
  { value: "rejected", label: "未通过" },
  { value: "deferred", label: "暂缓" },
];

export default function AdminCityBookClubApply() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [editingNote, setEditingNote] = useState({});
  const pageSize = 20;

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (status) params.set("status", status);
      if (keyword.trim()) params.set("keyword", keyword.trim());

      const res = await fetch(`/api/admin/book-club/city-apply?${params}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "加载失败");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setMessage(err.message || "加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  const updateItem = async (id, patch) => {
    setMessage("");
    try {
      const res = await fetch(`/api/admin/book-club/city-apply/${id}`, {
        method: "PATCH",
        headers: getAdminHeaders(true),
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "更新失败");
      setItems((prev) => prev.map((row) => (row.id === id ? data.item : row)));
      setMessage("已更新");
    } catch (err) {
      setMessage(err.message || "更新失败");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">城市读书会招募申请</h2>
        <p className="text-sm text-gray-500 mt-1">
          前台入口在「读书会 → 城市招募」。第一期以收表为主，可在此改状态并留备注。
        </p>
      </div>

      <div className="bg-white rounded-2xl border p-4 flex flex-wrap gap-3 items-end">
        <label className="text-sm">
          <span className="text-gray-500 block mb-1">状态</span>
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
            className="border rounded-xl px-3 py-2 min-w-[140px]"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm flex-1 min-w-[200px]">
          <span className="text-gray-500 block mb-1">搜索（姓名 / 手机 / 城市 / 微信）</span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                loadItems();
              }
            }}
            className="border rounded-xl px-3 py-2 w-full"
            placeholder="关键词"
          />
        </label>
        <button
          type="button"
          onClick={() => {
            setPage(1);
            loadItems();
          }}
          className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium"
        >
          查询
        </button>
      </div>

      {message && (
        <div className="text-sm text-blue-700 bg-blue-50 px-4 py-2 rounded-xl">{message}</div>
      )}

      <div className="bg-white rounded-2xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">申请人</th>
                <th className="px-4 py-3">城市</th>
                <th className="px-4 py-3">角色</th>
                <th className="px-4 py-3">联系方式</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3">提交时间</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    加载中...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    暂无申请
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <Fragment key={item.id}>
                    <tr className="border-t align-top">
                      <td className="px-4 py-3 text-gray-400">{item.id}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{item.name}</div>
                        <div className="text-xs text-gray-400">UID {item.user_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        {item.province}
                        {item.city}
                      </td>
                      <td className="px-4 py-3">{item.role_label}</td>
                      <td className="px-4 py-3">
                        <div>{item.phone}</div>
                        <div className="text-gray-500">微信 {item.wechat || "—"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={item.status}
                          onChange={(e) => updateItem(item.id, { status: e.target.value })}
                          className="border rounded-lg px-2 py-1.5"
                        >
                          {STATUS_OPTIONS.filter((o) => o.value).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {item.created_at ? item.created_at.replace("T", " ").slice(0, 16) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedId(expandedId === item.id ? null : item.id);
                            setEditingNote((prev) => ({
                              ...prev,
                              [item.id]: item.admin_note || "",
                            }));
                          }}
                          className="text-blue-600 hover:underline"
                        >
                          {expandedId === item.id ? "收起" : "详情"}
                        </button>
                      </td>
                    </tr>
                    {expandedId === item.id && (
                      <tr className="bg-slate-50 border-t">
                        <td colSpan={8} className="px-4 py-4 space-y-3">
                          <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700">
                            <p>
                              <span className="text-gray-400">邮箱：</span>
                              {item.email || "—"}
                            </p>
                            <p>
                              <span className="text-gray-400">参加过线上：</span>
                              {item.joined_online ? "是" : "否"}
                              {item.online_months ? `（${item.online_months}）` : ""}
                            </p>
                            <p className="md:col-span-2">
                              <span className="text-gray-400">可投入时间：</span>
                              {item.time_commitment || "—"}
                            </p>
                            <p className="md:col-span-2">
                              <span className="text-gray-400">动机：</span>
                              {item.motivation || "—"}
                            </p>
                            <p className="md:col-span-2">
                              <span className="text-gray-400">经验：</span>
                              {item.experience || "—"}
                            </p>
                            <p>
                              <span className="text-gray-400">预计人数：</span>
                              {item.expected_size || "—"}
                            </p>
                            <p>
                              <span className="text-gray-400">场地：</span>
                              {item.venue_note || "—"}
                            </p>
                            <p className="md:col-span-2">
                              <span className="text-gray-400">同工：</span>
                              {item.partners_note || "—"}
                            </p>
                            <p className="md:col-span-2">
                              <span className="text-gray-400">其他：</span>
                              {item.extra || "—"}
                            </p>
                          </div>
                          <div className="flex flex-col md:flex-row gap-2 items-start">
                            <textarea
                              value={editingNote[item.id] ?? item.admin_note ?? ""}
                              onChange={(e) =>
                                setEditingNote((prev) => ({
                                  ...prev,
                                  [item.id]: e.target.value,
                                }))
                              }
                              rows={2}
                              className="flex-1 w-full border rounded-xl px-3 py-2 text-sm"
                              placeholder="管理员备注"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                updateItem(item.id, {
                                  admin_note: editingNote[item.id] ?? "",
                                })
                              }
                              className="px-4 py-2 rounded-xl bg-gray-800 text-white text-sm"
                            >
                              保存备注
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          共 {total} 条 · 第 {page}/{totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border disabled:opacity-40"
          >
            上一页
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1.5 rounded-lg border disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  );
}
